import React from "react";
import ContentButton from "../core/ui/ContentButton";

// MRR Waterfall chart data — end-MRR line (left axis, $M) and monthly movement bars
// (right axis, $K) stacked as new logo / cross-sell above the baseline, downgrade / churn below.
const wfMonths = ["Aug '25", "Sep '25", "Oct '25", "Nov '25", "Dec '25", "Jan '26", "Feb '26", "Mar '26", "Apr '26", "May '26", "Jun '26", "Jul '26"];
const wfEndMrr = [1.22, 1.3, 1.42, 1.48, 1.55, 1.6, 1.64, 1.67, 1.7, 1.73, 1.75, 1.78];
const wfBars = [
  { newLogo: 60, cross: 18, down: -8, churn: -12 },
  { newLogo: 70, cross: 22, down: -10, churn: -16 },
  { newLogo: 95, cross: 30, down: -12, churn: -20 },
  { newLogo: 62, cross: 20, down: -16, churn: -30 },
  { newLogo: 78, cross: 26, down: -18, churn: -38 },
  { newLogo: 75, cross: 24, down: -15, churn: -32 },
  { newLogo: 60, cross: 20, down: -20, churn: -36 },
  { newLogo: 65, cross: 22, down: -18, churn: -40 },
  { newLogo: 70, cross: 24, down: -16, churn: -34 },
  { newLogo: 68, cross: 22, down: -20, churn: -44 },
  { newLogo: 72, cross: 24, down: -14, churn: -30 },
  { newLogo: 66, cross: 22, down: -16, churn: -20 },
];
const wfLeftTicks = [1.9, 1.7, 1.5, 1.3, 1.1];
const wfRightTicks = [161, 111, 61, 11, -39, -90];
const WF = { w: 920, h: 350, top: 24, bottom: 320, left: 70, right: 850 };
const wfYLeft = (v: number) => WF.top + ((1.9 - v) / 0.8) * (WF.bottom - WF.top);
const wfYRight = (v: number) => WF.top + ((161 - v) / 251) * (WF.bottom - WF.top);
const wfX = (i: number) => WF.left + 24 + i * ((WF.right - WF.left - 48) / (wfMonths.length - 1));

const MrrWaterfall: React.FC = () => {
  const linePath = wfEndMrr.map((v, i) => `${i === 0 ? "M" : "L"}${wfX(i)},${wfYLeft(v)}`).join(" ");
  const areaPath = `${linePath} L${wfX(wfEndMrr.length - 1)},${wfYRight(0)} L${wfX(0)},${wfYRight(0)} Z`;
  const barW = 30;
  return (
    <div className="hv-waterfall">
      <div className="hv-waterfall-title">MRR Waterfall</div>
      <svg viewBox={`0 0 ${WF.w} ${WF.h}`} role="img" aria-label="MRR waterfall chart">
        {wfLeftTicks.map((v) => (
          <text key={`l-${v}`} x={WF.left - 10} y={wfYLeft(v) + 4} textAnchor="end" fontSize="12" fill="#9ca3af">
            ${v.toFixed(1)}M
          </text>
        ))}
        {wfRightTicks.map((v) => (
          <text key={`r-${v}`} x={WF.right + 12} y={wfYRight(v) + 4} fontSize="12" fill="#9ca3af">
            ${v}K
          </text>
        ))}
        <text x={16} y={(WF.top + WF.bottom) / 2} fontSize="11" fill="#9ca3af" transform={`rotate(-90 16 ${(WF.top + WF.bottom) / 2})`} textAnchor="middle">
          End MRR
        </text>
        <text x={WF.w - 8} y={(WF.top + WF.bottom) / 2} fontSize="11" fill="#9ca3af" transform={`rotate(90 ${WF.w - 8} ${(WF.top + WF.bottom) / 2})`} textAnchor="middle">
          Monthly Movements
        </text>
        <line x1={WF.left} y1={wfYRight(0)} x2={WF.right} y2={wfYRight(0)} stroke="#d1d5db" strokeDasharray="4 4" />
        {wfBars.map((b, i) => {
          const x = wfX(i) - barW / 2;
          const zero = wfYRight(0);
          const crossTop = wfYRight(b.cross);
          const newTop = wfYRight(b.cross + b.newLogo);
          const downBottom = wfYRight(b.down);
          const churnBottom = wfYRight(b.down + b.churn);
          return (
            <g key={wfMonths[i]}>
              <rect x={x} y={newTop} width={barW} height={crossTop - newTop} fill="#4caf7d" rx="1.5" />
              <rect x={x} y={crossTop} width={barW} height={zero - crossTop} fill="#3aa6a0" />
              <rect x={x} y={zero} width={barW} height={downBottom - zero} fill="#e9a13b" />
              <rect x={x} y={downBottom} width={barW} height={churnBottom - downBottom} fill="#e05252" rx="1.5" />
            </g>
          );
        })}
        <path d={areaPath} fill="rgba(99, 91, 255, 0.08)" />
        <path d={linePath} fill="none" stroke="#635bff" strokeWidth="2.2" />
        {wfEndMrr.map((v, i) => (
          <circle key={`d-${wfMonths[i]}`} cx={wfX(i)} cy={wfYLeft(v)} r="4" fill="#fff" stroke="#635bff" strokeWidth="2" />
        ))}
        {wfMonths.map((m, i) => (
          <text key={m} x={wfX(i)} y={WF.h - 8} textAnchor="middle" fontSize="11.5" fill="#9ca3af">
            {m}
          </text>
        ))}
      </svg>
    </div>
  );
};

interface HomepageHeroProps {
  heading: React.ReactNode;
  lede: React.ReactNode;
  primaryLabel?: string;
  primaryHref?: string;
  secondaryLabel?: string;
  secondaryHref?: string;
}

const HomepageHero: React.FC<HomepageHeroProps> = ({
  heading,
  lede,
  primaryLabel = "Talk to Sales →",
  primaryHref = "https://calendar.app.google/rQuDdWM57HswfdSB9",
  secondaryLabel = "See how it works",
  secondaryHref = "#how-it-works",
}) => {
  const primaryIsExternal = /^https?:/.test(primaryHref);
  return (
    <section className="cs-hero" data-testid="home-hero">
      <div className="cs-hero-inner">
        <h1>{heading}</h1>

        <p className="cs-hero-lede">{lede}</p>

        <div className="cs-hero-actions">
          <ContentButton
            href={primaryHref}
            variant="primary"
            size="lg"
            target={primaryIsExternal ? "_blank" : undefined}
            rel={primaryIsExternal ? "noopener noreferrer" : undefined}
          >
            {primaryLabel}
          </ContentButton>
          <ContentButton href={secondaryHref} variant="secondary" size="lg">
            {secondaryLabel}
          </ContentButton>
        </div>

        <div className="cs-hero-visual">
          <div className="hv-chrome">
            <div className="dots"><span /><span /><span /></div>
            <div className="url">
              <span className="dim">app.datafabriq.co/</span>
              <span className="accent">acme-saas</span>
              <span className="dim">/dashboards/mrr-rollforward</span>
            </div>
          </div>

          <div className="hv-dashboard">
            <div className="hv-app-header">
              <h3>Dashboard – MRR Rollforward</h3>
              <div className="hv-app-header-right">
                <span className="picker">⊞ MRR Rollforward ▾</span>
                <span className="share">⇧ Share</span>
              </div>
            </div>

            <div className="hv-drill-layout">
              <div className="hv-table-card">
                <div className="hv-table-title">Monthly MRR Rollforward</div>
                <div className="hv-table-sub">
                  Revenue movement by customer <span className="pipe">|</span> Excludes Acme,
                  Lattice Labs <span className="pipe">|</span> Volatile usage customers flagged
                </div>

                <div className="hv-tabs">
                  <span className="hv-tab-i">Totals</span>
                  <span className="hv-tab-i">Total MRR</span>
                  <span className="hv-tab-i">New Logo</span>
                  <span className="hv-tab-i active">Cross-sell</span>
                  <span className="hv-tab-i">Price/Upsell</span>
                  <span className="hv-tab-i">Downgrade</span>
                  <span className="hv-tab-i">Churn</span>
                  <span className="hv-tab-i">Exceptions</span>
                </div>

                <div className="hv-hint">
                  Click any value to see the audit trail and propose an override.
                </div>

                <table className="hv-rf-table">
                  <thead>
                    <tr>
                      <th>Customer</th>
                      <th>Jan '25</th>
                      <th>Feb '25</th>
                      <th>Mar '25</th>
                      <th>Apr '25</th>
                      <th>May '25</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr><td>Aperture Solutions</td><td>0</td><td>0</td><td>0</td><td>0</td><td>0</td></tr>
                    <tr><td>Acme Inc.</td><td className="val-blue">1,212</td><td>0</td><td>0</td><td>0</td><td>0</td></tr>
                    <tr><td>Banyan Software</td><td>0</td><td>0</td><td>0</td><td>0</td><td>0</td></tr>
                    <tr><td>Beam Living</td><td>0</td><td>0</td><td className="val-blue">1,269</td><td>0</td><td>0</td></tr>
                    <tr><td>Cascade Systems</td><td>0</td><td>0</td><td>0</td><td>0</td><td>0</td></tr>
                    <tr><td>Cedar Ridge</td><td>0</td><td>0</td><td>0</td><td>0</td><td>0</td></tr>
                    <tr className="highlighted"><td>Cirrus Labs</td><td>0</td><td>0</td><td>0</td><td>0</td><td className="val-blue">2,104</td></tr>
                    <tr><td>Delta Cloud</td><td className="val-blue">360</td><td>0</td><td>0</td><td>0</td><td>0</td></tr>
                    <tr><td>Echo Industries</td><td className="val-blue">1,206</td><td>0</td><td>0</td><td>0</td><td>0</td></tr>
                    <tr><td>Fathom Data</td><td>0</td><td>0</td><td>0</td><td>0</td><td className="val-blue">433</td></tr>
                    <tr><td>Glacier Labs</td><td className="val-blue">435</td><td>0</td><td>0</td><td>0</td><td>0</td></tr>
                  </tbody>
                </table>
              </div>

              <div className="hv-drill-card">
                <div className="hv-drill-head">
                  <span className="lb">Entry Audit — May 2025</span>
                  <span className="cl">×</span>
                </div>

                <div>
                  <div className="hv-drill-customer">Cirrus Labs</div>
                  <div className="hv-drill-tags">
                    <span className="hv-drill-tag cross-sell">Cross-sell</span>
                    <span className="hv-drill-tag medium">Medium</span>
                  </div>
                </div>

                <div className="hv-drill-stats">
                  <div className="hv-drill-stat">
                    <div className="k">Beginning</div>
                    <div className="v">$2,965</div>
                  </div>
                  <div className="hv-drill-stat pos">
                    <div className="k">Ending</div>
                    <div className="v">$5,069</div>
                  </div>
                  <div className="hv-drill-stat pos">
                    <div className="k">Impact</div>
                    <div className="v">$2,104</div>
                  </div>
                </div>

                <div>
                  <div className="hv-logic-head" style={{ marginBottom: 10 }}>Classification Logic</div>
                  <div className="hv-logic">
                    <div className="hv-logic-step">
                      <div className="dot" />
                      <div className="line">
                        <div className="main">Ending $5,069 &gt; Beginning $2,965</div>
                        <div className="sub">MRR increased</div>
                      </div>
                      <span className="badge">PASS</span>
                    </div>
                    <div className="hv-logic-step">
                      <div className="dot" />
                      <div className="line">
                        <div className="main">Increase = $2,104 (71.0%)</div>
                        <div className="sub">&gt;$50 AND &gt;5% threshold check</div>
                      </div>
                      <span className="badge">PASS</span>
                    </div>
                    <div className="hv-logic-step">
                      <div className="dot" />
                      <div className="line">
                        <div className="main">Matched to Salesforce opportunity</div>
                        <div className="sub">SF opp_40219 · Renewal / Cross-sell</div>
                      </div>
                      <span className="badge">PASS</span>
                    </div>
                    <div className="hv-logic-step result">
                      <div className="dot" />
                      <div className="line">
                        <div className="main">Cross-sell value = $2,104</div>
                        <div className="sub">Final valuation</div>
                      </div>
                      <span className="badge">RESULT</span>
                    </div>
                  </div>
                </div>

                <div className="hv-override">
                  <div>
                    <div className="t">Override this entry</div>
                    <div className="s">Generates a directive for chat.</div>
                  </div>
                  <button className="hv-override-btn">Start</button>
                </div>

                <div>
                  <div className="hv-sf-head" style={{ marginBottom: 8 }}>Salesforce Context</div>
                  <div className="hv-sf">
                    <div className="hv-sf-cell"><span className="k">Record Type</span><span className="v">Renewal</span></div>
                    <div className="hv-sf-cell"><span className="k">Deal Type</span><span className="v">Renewal</span></div>
                    <div className="hv-sf-cell"><span className="k">Platform</span><span className="v">Platform</span></div>
                    <div className="hv-sf-cell"><span className="k">Opportunity</span><span className="v link">View in SF →</span></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <MrrWaterfall />
        </div>
      </div>
    </section>
  );
};

export default HomepageHero;
